import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Palette, Eye, Upload, Trash2, Image as ImageIcon,
  Globe, Shield, ExternalLink, Info, CheckCircle, AlertCircle, ShoppingCart,
} from 'lucide-react';
import { DomainRequestModal } from '@/components/DomainRequestModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'branding', label: 'Branding', icon: Palette },
  { key: 'domain', label: 'Domain Setup', icon: Globe },
  { key: 'ssl', label: 'SSL Setup', icon: Shield },
];

export default function ResellerWhiteLabel() {
  const [activeTab, setActiveTab] = useState('branding');

  // ── Branding state ────────────────────────────────────────────────────────
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    primary_color: '#0f172a',
    accent_color: '#3b82f6',
    logo_url: '',
    show_pricing: true,
    company_name: '',
  });

  // ── Domain state ──────────────────────────────────────────────────────────
  const [domainOption, setDomainOption] = useState(null); // 'subdomain' | 'custom' | 'buy'
  const [domainInput, setDomainInput] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainMsg, setDomainMsg] = useState(null);
  const [dnsRecords, setDnsRecords] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Domain Request Modal
  const [modalOpen, setModalOpen]             = useState(false);
  const [pendingDomain, setPendingDomain]     = useState('');
  const [pendingPrice, setPendingPrice]       = useState(0);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/white-label/settings`);
      setSettings(response.data);
      setFormData({
        primary_color: response.data.primary_color || '#0f172a',
        accent_color: response.data.accent_color || '#3b82f6',
        logo_url: response.data.logo_url || '',
        show_pricing: response.data.show_pricing ?? true,
        company_name: response.data.company_name || '',
      });
    } catch {
      toast.error('Failed to fetch white-label settings');
    } finally {
      setLoading(false);
    }
  };

  // ── Logo handlers ─────────────────────────────────────────────────────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { toast.error('Invalid file type. Allowed: PNG, JPG, GIF, WEBP'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('File size must be less than 2MB'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await axios.post(`${API}/white-label/upload-logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo uploaded successfully!');
      setFormData(prev => ({ ...prev, logo_url: response.data.logo_url }));
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/white-label/delete-logo`);
      toast.success('Logo removed successfully');
      setFormData(prev => ({ ...prev, logo_url: '' }));
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete logo');
    }
  };

  const getLogoDisplayUrl = (logoUrl) => {
    if (!logoUrl) return null;
    return logoUrl.startsWith('/') ? `${BACKEND_URL}${logoUrl}` : logoUrl;
  };

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/white-label/settings`, formData);
      toast.success('White-label settings updated successfully');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    }
  };

  // ── Domain handlers ───────────────────────────────────────────────────────
  const resetDomainState = (key) => {
    setDomainOption(key);
    setDomainInput('');
    setDomainMsg(null);
    setDnsRecords(null);
    setSearchResults(null);
  };

  const handleSetSubdomain = async () => {
    if (!domainInput.trim()) return;
    setDomainLoading(true); setDomainMsg(null);
    try {
      const res = await axios.put(`${API}/white-label/domain/subdomain`, {
        subdomain: domainInput.trim().toLowerCase(),
      });
      setDomainMsg({ type: 'success', text: `Activated! Your storefront: ${res.data.url}` });
      fetchSettings();
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to set subdomain' });
    } finally { setDomainLoading(false); }
  };

  const handleSetCustomDomain = async () => {
    if (!domainInput.trim()) return;
    setDomainLoading(true); setDomainMsg(null); setDnsRecords(null);
    try {
      const res = await axios.put(`${API}/white-label/domain/custom`, { domain: domainInput.trim() });
      setDnsRecords(res.data.dns_records);
      setDomainMsg({ type: 'info', text: res.data.message });
      fetchSettings();
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to save domain' });
    } finally { setDomainLoading(false); }
  };

  const handleVerifyDNS = async () => {
    setVerifying(true); setDomainMsg(null);
    try {
      const res = await axios.post(`${API}/white-label/domain/verify`);
      if (res.data.verified) {
        setDomainMsg({ type: 'success', text: res.data.message });
        setDnsRecords(null);
        fetchSettings();
      } else {
        setDomainMsg({ type: 'error', text: res.data.message });
      }
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Verification failed' });
    } finally { setVerifying(false); }
  };

  const handleSearchDomains = async () => {
    if (!domainInput.trim()) return;
    setSearching(true); setSearchResults(null); setDomainMsg(null);
    try {
      const res = await axios.get(`${API}/white-label/domain/search`, { params: { name: domainInput.trim() } });
      setSearchResults(res.data.domains || []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const isNetworkError = !e.response || e.code === 'ERR_NETWORK';
      const msg = isNetworkError
        ? 'Domain search requires internet access to ResellerClub. This works on the live production domain.'
        : (detail || 'Domain search is unavailable right now. This feature works on the live production domain.');
      setDomainMsg({ type: 'error', text: msg });
    } finally { setSearching(false); }
  };

  const handleBuyDomain = (domain, price) => {
    setPendingDomain(domain);
    setPendingPrice(price || 0);
    setModalOpen(true);
  };

  const handleModalSubmit = async (payload) => {
    setModalSubmitting(true);
    try {
      await axios.post(`${API}/domain/request`, payload);
      setModalOpen(false);
      setSearchResults(null);
      setDomainMsg({
        type: 'success',
        text: "Your domain request has been submitted. You'll be notified once it's approved.",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request submission failed');
    } finally {
      setModalSubmitting(false);
    }
  };

  const presetColors = [
    { name: 'Slate Blue', primary: '#0f172a', accent: '#3b82f6' },
    { name: 'Forest Green', primary: '#064e3b', accent: '#10b981' },
    { name: 'Purple', primary: '#581c87', accent: '#a855f7' },
    { name: 'Orange', primary: '#7c2d12', accent: '#f97316' },
    { name: 'Teal', primary: '#134e4a', accent: '#14b8a6' },
  ];

  const isDomainLive =
    settings?.domain_status === 'active' || settings?.domain_status === 'verified';
  const isDomainPending = settings?.domain_status === 'pending_verification';

  return (
    <DashboardLayout role="reseller">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="mb-2">
          <h1 className="text-4xl font-bold tracking-tight" data-testid="white-label-page-title">
            White Label Settings
          </h1>
          <p className="text-slate-500 mt-2">Customize your brand and configure your domain</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    data-testid={`wl-tab-${tab.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === tab.key
                        ? 'bg-white text-slate-800 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* ── BRANDING TAB ────────────────────────────────────────────── */}
            {activeTab === 'branding' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card data-testid="settings-form-card">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Palette className="h-5 w-5" />
                      <span>Customization</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSaveBranding} className="space-y-6">
                      {/* Storefront URL — always visible */}
                      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-1">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Your Storefront URL</p>
                        {settings?.subdomain ? (
                          <a
                            href={`/public/${settings.subdomain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm font-mono block"
                            data-testid="subdomain-link"
                          >
                            {settings.subdomain}.cloudsetu.com
                          </a>
                        ) : (
                          <p className="text-sm text-slate-400 italic">
                            Not configured — go to <button type="button" className="text-blue-600 underline" onClick={() => setActiveTab('domain')}>Domain Setup</button>
                          </p>
                        )}
                      </div>

                      {/* Company Name */}
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Company Name</Label>
                        <Input
                          id="company_name"
                          value={formData.company_name}
                          onChange={e => setFormData(p => ({ ...p, company_name: e.target.value }))}
                          placeholder="Your company name"
                          data-testid="wl-company-name-input"
                        />
                        <p className="text-xs text-slate-400">Shown in your storefront header and footer</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="primary_color">Primary Color</Label>
                        <div className="flex space-x-2">
                          <Input id="primary_color" type="color" value={formData.primary_color}
                            onChange={e => setFormData(p => ({ ...p, primary_color: e.target.value }))}
                            className="w-20 h-10" data-testid="primary-color-input" />
                          <Input type="text" value={formData.primary_color}
                            onChange={e => setFormData(p => ({ ...p, primary_color: e.target.value }))}
                            className="flex-1" data-testid="primary-color-text-input" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="accent_color">Accent Color</Label>
                        <div className="flex space-x-2">
                          <Input id="accent_color" type="color" value={formData.accent_color}
                            onChange={e => setFormData(p => ({ ...p, accent_color: e.target.value }))}
                            className="w-20 h-10" data-testid="accent-color-input" />
                          <Input type="text" value={formData.accent_color}
                            onChange={e => setFormData(p => ({ ...p, accent_color: e.target.value }))}
                            className="flex-1" data-testid="accent-color-text-input" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Color Presets</Label>
                        <div className="grid grid-cols-5 gap-2">
                          {presetColors.map((preset, index) => (
                            <button key={index} type="button"
                              onClick={() => setFormData(p => ({ ...p, primary_color: preset.primary, accent_color: preset.accent }))}
                              className="flex flex-col items-center p-2 border rounded-md hover:border-blue-500 transition-colors"
                              title={preset.name} data-testid={`color-preset-${index}`}>
                              <div className="flex space-x-1">
                                <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.primary }}></div>
                                <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.accent }}></div>
                              </div>
                              <span className="text-xs mt-1 text-slate-600">{preset.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Your Logo</Label>
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
                          {formData.logo_url ? (
                            <div className="flex items-center gap-4">
                              <div className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center">
                                <img src={getLogoDisplayUrl(formData.logo_url)} alt="Your logo"
                                  className="w-full h-full object-contain p-2"
                                  onError={e => { e.target.style.display = 'none'; }} />
                              </div>
                              <div className="flex-1 space-y-2">
                                <p className="text-sm text-slate-600">Your custom logo is set</p>
                                <div className="flex gap-2">
                                  <Button type="button" variant="outline" size="sm"
                                    onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                    data-testid="change-logo-btn">
                                    <Upload className="h-4 w-4 mr-2" /> Change
                                  </Button>
                                  <Button type="button" variant="destructive" size="sm"
                                    onClick={handleDeleteLogo} data-testid="delete-logo-btn">
                                    <Trash2 className="h-4 w-4 mr-2" /> Remove
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-6 cursor-pointer hover:bg-slate-50 rounded-lg transition-colors"
                              onClick={() => fileInputRef.current?.click()}>
                              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                                <ImageIcon className="h-8 w-8 text-slate-400" />
                              </div>
                              <p className="text-sm font-medium text-slate-700">Upload your logo</p>
                              <p className="text-xs text-slate-500 mt-1">PNG, JPG, GIF up to 2MB</p>
                              <Button type="button" variant="outline" size="sm" className="mt-3"
                                disabled={uploading} data-testid="upload-logo-btn">
                                <Upload className="h-4 w-4 mr-2" />
                                {uploading ? 'Uploading...' : 'Select File'}
                              </Button>
                            </div>
                          )}
                          <input ref={fileInputRef} type="file"
                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                            onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
                        </div>
                        <p className="text-xs text-slate-500">
                          Your logo will appear in the sidebar and header of your dashboard
                        </p>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input type="checkbox" id="show_pricing" checked={formData.show_pricing}
                          onChange={e => setFormData(p => ({ ...p, show_pricing: e.target.checked }))}
                          className="w-4 h-4" data-testid="show-pricing-checkbox" />
                        <Label htmlFor="show_pricing">Show pricing on public page</Label>
                      </div>

                      <Button type="submit" className="w-full" data-testid="save-settings-button">
                        Save Settings
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Preview Card */}
                <Card data-testid="preview-card">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Eye className="h-5 w-5" />
                      <span>Live Preview</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-6 flex items-center gap-4" style={{ backgroundColor: formData.primary_color }}>
                        {formData.logo_url && (
                          <div className="w-12 h-12 bg-white rounded-lg overflow-hidden flex items-center justify-center">
                            <img src={getLogoDisplayUrl(formData.logo_url)} alt="Logo preview"
                              className="w-full h-full object-contain p-1"
                              onError={e => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                        <h2 className="text-2xl font-bold text-white">{formData.company_name || settings?.company_name}</h2>
                      </div>
                      <div className="p-6 bg-white space-y-4">
                        <div className="border rounded-lg p-4">
                          <h3 className="font-semibold mb-2">Sample Product</h3>
                          <p className="text-sm text-slate-600 mb-3">Shared Hosting - Basic Plan</p>
                          <button className="px-4 py-2 rounded-md text-white font-medium"
                            style={{ backgroundColor: formData.accent_color }}>
                            Order Now
                          </button>
                        </div>
                        <p className="text-xs text-slate-500">This is how your white-label page will look</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ── DOMAIN SETUP TAB ─────────────────────────────────────────── */}
            {activeTab === 'domain' && (
              <div className="space-y-6 max-w-3xl">
                {/* Active domain banner */}
                {isDomainLive && (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center justify-between"
                    data-testid="domain-active-banner">
                    <div>
                      <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        {settings.domain_type === 'subdomain'
                          ? `${settings.subdomain}.cloudsetu.com`
                          : settings.custom_domain} — Active
                      </p>
                      <p className="text-xs text-green-600 mt-0.5">
                        Your white-label domain is live. Clients will see your branding.
                      </p>
                    </div>
                    <a
                      href={settings.domain_type === 'subdomain'
                        ? `/public/${settings.subdomain}`
                        : `https://${settings.custom_domain}/public/${settings.subdomain || ''}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-green-700 font-medium hover:underline"
                      data-testid="wl-preview-link">
                      <ExternalLink className="h-3.5 w-3.5" /> Preview Storefront
                    </a>
                  </div>
                )}

                {isDomainPending && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 text-sm text-amber-800 font-medium flex items-center gap-2"
                    data-testid="domain-pending-banner">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    DNS verification pending for <b>{settings.custom_domain}</b>. Add the CNAME records below and click Verify DNS.
                  </div>
                )}

                {/* 3 Option cards */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-100">
                    <h2 className="font-semibold text-slate-800">Domain Setup</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Choose how your clients access your white-label portal
                    </p>
                  </div>

                  {/* Option selector */}
                  <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { key: 'subdomain', label: 'Free Subdomain', desc: 'mycompany.cloudsetu.com', badge: 'Free', color: 'blue' },
                      { key: 'custom', label: 'Your Own Domain', desc: 'mycompany.com', badge: 'Advanced', color: 'violet' },
                      { key: 'buy', label: 'Buy a Domain', desc: 'Search & purchase new', badge: 'Premium', color: 'amber' },
                    ].map(opt => (
                      <button key={opt.key}
                        onClick={() => resetDomainState(opt.key)}
                        data-testid={`wl-option-${opt.key}`}
                        className={`relative rounded-xl border-2 p-4 text-left transition-all ${domainOption === opt.key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                        <span className={`absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full
                          ${opt.color === 'blue' ? 'bg-blue-100 text-blue-700'
                            : opt.color === 'violet' ? 'bg-violet-100 text-violet-700'
                            : 'bg-amber-100 text-amber-700'}`}>
                          {opt.badge}
                        </span>
                        <p className="font-semibold text-slate-800 text-sm">{opt.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5 font-mono">{opt.desc}</p>
                      </button>
                    ))}
                  </div>

                  {/* Option: Subdomain */}
                  {domainOption === 'subdomain' && (
                    <div className="px-6 pb-6 space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">
                          Enter your subdomain name
                        </label>
                        <div className="flex gap-2">
                          <div className="flex flex-1 rounded-lg border border-slate-200 overflow-hidden">
                            <input value={domainInput}
                              onChange={e => setDomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                              placeholder="mycompany"
                              className="flex-1 px-3 py-2.5 text-sm outline-none"
                              data-testid="wl-subdomain-input" />
                            <span className="px-3 py-2.5 bg-slate-50 border-l border-slate-200 text-sm text-slate-500">
                              .cloudsetu.com
                            </span>
                          </div>
                          <Button onClick={handleSetSubdomain} disabled={domainLoading || !domainInput}
                            className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="wl-subdomain-submit">
                            {domainLoading ? 'Saving…' : 'Activate'}
                          </Button>
                        </div>
                        {domainInput && (
                          <p className="text-xs text-slate-400 mt-1">
                            Preview: <b>{domainInput}.cloudsetu.com</b>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Option: Custom Domain */}
                  {domainOption === 'custom' && (
                    <div className="px-6 pb-6 space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">
                          Enter your domain
                        </label>
                        <div className="flex gap-2">
                          <input value={domainInput}
                            onChange={e => setDomainInput(e.target.value.toLowerCase())}
                            placeholder="mycompany.com"
                            className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid="wl-custom-domain-input" />
                          <Button onClick={handleSetCustomDomain} disabled={domainLoading || !domainInput}
                            className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="wl-custom-domain-submit">
                            {domainLoading ? 'Saving…' : 'Get DNS Records'}
                          </Button>
                        </div>
                      </div>

                      {/* DNS Records Table */}
                      {(dnsRecords || isDomainPending) && (
                        <div className="rounded-xl border border-slate-200 overflow-hidden">
                          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                            <p className="text-xs font-semibold text-slate-700">DNS Records to Add in Your Registrar</p>
                            <Button size="sm" variant="outline" onClick={handleVerifyDNS} disabled={verifying}
                              className="text-xs h-7" data-testid="wl-verify-dns-btn">
                              {verifying ? 'Checking…' : 'Verify DNS'}
                            </Button>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-slate-100">
                                  {['Type', 'Name', 'Value', 'TTL'].map(h => (
                                    <th key={h} className="text-left px-4 py-2 text-slate-500 font-medium">{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(dnsRecords || [
                                  { type: 'CNAME', name: '@', value: 'connectdesk.cloudsetu.com', ttl: '3600' },
                                  { type: 'CNAME', name: 'www', value: 'connectdesk.cloudsetu.com', ttl: '3600' },
                                ]).map((rec, i) => (
                                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                                    <td className="px-4 py-2.5 font-mono font-bold text-violet-700">{rec.type}</td>
                                    <td className="px-4 py-2.5 font-mono">{rec.name}</td>
                                    <td className="px-4 py-2.5 font-mono text-blue-700 max-w-[220px] truncate">{rec.value}</td>
                                    <td className="px-4 py-2.5 text-slate-400">{rec.ttl}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <p className="px-4 py-2.5 text-[11px] text-slate-400">
                            DNS propagation can take 5 min – 48 hrs. Click "Verify DNS" after adding the records.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Option: Buy Domain */}
                  {domainOption === 'buy' && (
                    <div className="px-6 pb-6 space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">
                          Search for a domain
                        </label>
                        <div className="flex gap-2">
                          <input value={domainInput}
                            onChange={e => setDomainInput(e.target.value.toLowerCase())}
                            placeholder="mycompany"
                            className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:ring-2 focus:ring-amber-500"
                            onKeyDown={e => e.key === 'Enter' && handleSearchDomains()}
                            data-testid="wl-domain-search-input" />
                          <Button onClick={handleSearchDomains} disabled={searching || !domainInput}
                            className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="wl-domain-search-btn">
                            {searching ? 'Searching…' : 'Search'}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-400 mt-1.5">
                          Note: Domain search requires production environment. May not work in preview.
                        </p>
                      </div>
                      {searchResults && (
                        <div className="space-y-2">
                          {searchResults.length === 0
                            ? <p className="text-sm text-slate-500">No results found.</p>
                            : searchResults.map((d, i) => (
                              <div key={i}
                                className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
                                  d.available ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50 opacity-60'
                                }`}
                                data-testid={`domain-result-${i}`}>
                                <div>
                                  <p className="text-sm font-mono font-semibold text-slate-800">{d.domainName}</p>
                                  <p className={`text-xs mt-0.5 ${d.available ? 'text-green-600' : 'text-slate-400'}`}>
                                    {d.available ? 'Available' : 'Unavailable'}
                                  </p>
                                </div>
                                {d.available && (
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold text-slate-800">
                                      ₹{Number(d.price || 0).toLocaleString('en-IN')}/yr
                                    </span>
                                    <Button size="sm" onClick={() => handleBuyDomain(d.domainName, d.price)}
                                      disabled={domainLoading}
                                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 gap-1"
                                      data-testid={`buy-domain-btn-${i}`}>
                                      <ShoppingCart className="w-3 h-3" />
                                      Request
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  )}

                  {/* Domain message */}
                  {domainMsg && (
                    <div className={`mx-6 mb-5 rounded-lg px-4 py-3 text-sm ${
                      domainMsg.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : domainMsg.type === 'error'
                          ? 'bg-red-50 border border-red-200 text-red-700'
                          : 'bg-blue-50 border border-blue-200 text-blue-800'
                    }`} data-testid="wl-domain-msg">
                      {domainMsg.text}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── SSL SETUP TAB ─────────────────────────────────────────────── */}
            {activeTab === 'ssl' && (
              <div className="max-w-3xl space-y-6">
                {/* Current domain status */}
                {isDomainLive && settings?.domain_type !== 'subdomain' ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center gap-3"
                    data-testid="ssl-domain-verified">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        DNS Verified: {settings.custom_domain}
                      </p>
                      <p className="text-xs text-green-600 mt-0.5">
                        Your domain is pointing correctly to CloudSetu. You can now request SSL.
                      </p>
                    </div>
                  </div>
                ) : settings?.domain_type === 'subdomain' ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 flex items-center gap-3">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-blue-800">
                        Using CloudSetu Subdomain ({settings.subdomain}.cloudsetu.com)
                      </p>
                      <p className="text-xs text-blue-600 mt-0.5">
                        Subdomains are automatically covered by CloudSetu's wildcard SSL. No action needed.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">No Domain Configured Yet</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Please set up your domain in the "Domain Setup" tab first before requesting SSL.
                      </p>
                    </div>
                  </div>
                )}

                {/* SSL Instructions */}
                <Card data-testid="ssl-instructions-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-600" />
                      SSL Certificate Setup Instructions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <p className="text-sm text-slate-600">
                      SSL certificates for custom domains are managed manually by the CloudSetu admin team.
                      Follow the steps below to get HTTPS enabled for your custom domain.
                    </p>

                    <ol className="space-y-4">
                      {[
                        {
                          step: '1',
                          title: 'Configure Your Custom Domain',
                          desc: 'Go to the "Domain Setup" tab and enter your custom domain. Click "Get DNS Records" to receive the CNAME records.',
                          color: 'blue',
                        },
                        {
                          step: '2',
                          title: 'Add DNS Records at Your Registrar',
                          desc: 'Log into your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.) and add the CNAME records provided. DNS changes can take 5 minutes to 48 hours.',
                          color: 'violet',
                        },
                        {
                          step: '3',
                          title: 'Verify DNS',
                          desc: 'Once DNS has propagated, go back to "Domain Setup" and click "Verify DNS". This confirms your domain is pointing to CloudSetu.',
                          color: 'cyan',
                        },
                        {
                          step: '4',
                          title: 'Request SSL Certificate',
                          desc: 'After DNS is verified, contact the CloudSetu admin team via the Support section and raise a ticket: "SSL Certificate Request for [yourdomain.com]". Admin will provision the certificate within 24 hours.',
                          color: 'green',
                          highlight: true,
                        },
                        {
                          step: '5',
                          title: 'HTTPS Activated',
                          desc: 'Once SSL is issued, your domain will serve over HTTPS automatically. Your clients will see a secure padlock in their browser.',
                          color: 'emerald',
                        },
                      ].map((item) => (
                        <li key={item.step}
                          className={`flex gap-4 p-4 rounded-xl ${item.highlight ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50'}`}>
                          <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white
                            ${item.color === 'blue' ? 'bg-blue-500'
                              : item.color === 'violet' ? 'bg-violet-500'
                              : item.color === 'cyan' ? 'bg-cyan-500'
                              : item.color === 'green' ? 'bg-green-500'
                              : 'bg-emerald-500'}`}>
                            {item.step}
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{item.title}</p>
                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{item.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ol>

                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                        <Info className="h-3.5 w-3.5" /> Important Notes
                      </p>
                      <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                        <li>SSL certificates are free for all verified partners.</li>
                        <li>Wildcard SSL is available for *.cloudsetu.com subdomains automatically.</li>
                        <li>Custom domain SSL is manually issued — plan at least 24 hours in advance.</li>
                        <li>Domains purchased via "Buy a Domain" get SSL configured automatically.</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>

      {/* Domain Request Modal */}
      <DomainRequestModal
        open={modalOpen}
        onClose={() => !modalSubmitting && setModalOpen(false)}
        domain={pendingDomain}
        years={1}
        price={pendingPrice}
        isEmailOnly={false}
        onSubmit={handleModalSubmit}
        submitting={modalSubmitting}
      />
    </DashboardLayout>
  );
}
